
import React from 'react';
import { TrendingUp, TrendingDown, Users, Clock, CreditCard, Tag, Heart, BarChart2, PieChart } from 'lucide-react';

const SegmentProfile = ({ selectedSegment, includeCustomerSatisfaction = false }) => {
  const getSegmentColor = (segmentId) => {
    const colors = {
      urban: 'purple',
      highway: 'blue',
      rural: 'green',
      suburban: 'amber'
    };
    return colors[segmentId] || 'purple';
  };
  
  const getClvStatus = (clv, target) => {
    if (clv >= target) return 'above';
    if (clv >= target * 0.9) return 'near';
    return 'below';
  };
  
  const getStatusColor = (status) => {
    if (status === 'above') return 'text-green-600';
    if (status === 'near') return 'text-yellow-600';
    return 'text-red-600';
  };
  
  const getStatusBg = (status) => {
    if (status === 'above') return 'bg-green-100';
    if (status === 'near') return 'bg-yellow-100';
    return 'bg-red-100';
  };
  
  const getStatusIcon = (status) => {
    if (status === 'above') return <TrendingUp size={14} className="mr-1" />;
    if (status === 'near') return <TrendingUp size={14} className="mr-1 rotate-45" />;
    return <TrendingDown size={14} className="mr-1" />;
  };
  
  const getShareOfWalletStatus = (share) => {
    if (share >= 75) return 'above';
    if (share >= 60) return 'near';
    return 'below';
  };
  
  const clvStatus = getClvStatus(selectedSegment.clv, selectedSegment.targetCLV);
  const shareStatus = getShareOfWalletStatus(selectedSegment.shareOfWallet);
  const color = getSegmentColor(selectedSegment?.id || 'urban');
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
      {/* Traffic Card */}
      <div className={`bg-white rounded-lg p-4 shadow border-t-4 border-${color}-500`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-medium text-gray-800">Daily Traffic</h3>
          <div className={`w-8 h-8 rounded-full bg-${color}-100 flex items-center justify-center`}>
            <Users size={16} className={`text-${color}-600`} />
          </div>
        </div>
        <div className="mt-1">
          <div className="text-2xl font-bold text-gray-900">{selectedSegment.traffic.toLocaleString()}</div>
          <div className="flex items-center mt-2 text-xs">
            <div className={`px-2 py-1 rounded-full ${selectedSegment.traffic > 800 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'} flex items-center`}>
              {selectedSegment.traffic > 800 ? (
                <TrendingUp size={12} className="mr-1" />
              ) : (
                <TrendingDown size={12} className="mr-1" />
              )}
              {selectedSegment.traffic > 800 ? '+' : ''}{selectedSegment.traffic > 800 ? '12' : '-3'}% vs avg
            </div>
          </div>
        </div>
      </div>
      
      {/* Basket Size */}
      <div className={`bg-white rounded-lg p-4 shadow border-t-4 border-${color}-500`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-medium text-gray-800">Basket Size</h3>
          <div className={`w-8 h-8 rounded-full bg-${color}-100 flex items-center justify-center`}>
            <CreditCard size={16} className={`text-${color}-600`} />
          </div>
        </div>
        <div className="mt-1">
          <div className="text-2xl font-bold text-gray-900">฿{selectedSegment.basketSize.toLocaleString()}</div>
          <div className="flex items-center mt-2 text-xs">
            <div className={`px-2 py-1 rounded-full ${selectedSegment.basketSize > 700 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'} flex items-center`}>
              {selectedSegment.basketSize > 700 ? (
                <TrendingUp size={12} className="mr-1" />
              ) : (
                <TrendingDown size={12} className="mr-1" />
              )}
              {selectedSegment.basketSize > 700 ? '+' : ''}{selectedSegment.basketSize > 700 ? '5' : '-2'}% vs avg
            </div>
          </div>
        </div>
      </div>
      
      {/* Conversion Rate */}
      <div className={`bg-white rounded-lg p-4 shadow border-t-4 border-${color}-500`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-medium text-gray-800">Conversion Rate</h3>
          <div className={`w-8 h-8 rounded-full bg-${color}-100 flex items-center justify-center`}>
            <Tag size={16} className={`text-${color}-600`} />
          </div>
        </div>
        <div className="mt-1">
          <div className="text-2xl font-bold text-gray-900">{selectedSegment.conversionRate}%</div>
          <div className="flex items-center mt-2 text-xs">
            <div className={`px-2 py-1 rounded-full ${selectedSegment.conversionRate > 60 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'} flex items-center`}>
              {selectedSegment.conversionRate > 60 ? (
                <TrendingUp size={12} className="mr-1" />
              ) : (
                <TrendingDown size={12} className="mr-1" />
              )}
              {selectedSegment.conversionRate > 60 ? '+' : ''}{selectedSegment.conversionRate > 60 ? '3' : '-1'}% vs avg
            </div>
          </div>
        </div>
      </div>
      
      {/* Customer Lifetime Value */}
      <div className={`bg-white rounded-lg p-4 shadow border-t-4 border-${color}-500`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-medium text-gray-800">Customer Lifetime Value</h3>
          <div className={`w-8 h-8 rounded-full bg-${color}-100 flex items-center justify-center`}>
            <Heart size={16} className={`text-${color}-600`} />
          </div>
        </div>
        <div className="mt-1">
          <div className="text-2xl font-bold text-gray-900">{selectedSegment.clv.toFixed(1)}x</div>
          <div className="flex items-center mt-2 text-xs">
            <div className={`px-2 py-1 rounded-full ${getStatusBg(clvStatus)} ${getStatusColor(clvStatus)} flex items-center`}>
              {getStatusIcon(clvStatus)}
              {clvStatus === 'above' ? '+' : clvStatus === 'below' ? '-' : ''}{Math.abs((selectedSegment.clv - selectedSegment.targetCLV) * 10).toFixed(0)}% vs target
            </div>
          </div>
        </div>
      </div>
      
      {/* Additional cards in the second row */}
      <div className={`bg-white rounded-lg p-4 shadow border-t-4 border-${color}-500`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-medium text-gray-800">Peak Hours</h3>
          <div className={`w-8 h-8 rounded-full bg-${color}-100 flex items-center justify-center`}>
            <Clock size={16} className={`text-${color}-600`} />
          </div>
        </div>
        <div className="mt-1">
          <div className="text-2xl font-bold text-gray-900">{selectedSegment.peakHours}</div>
          <div className="flex items-center mt-2 text-xs text-gray-600">
            Highest transaction volume period
          </div>
        </div>
      </div>
      
      {/* Share of Wallet vs Competitors */}
      <div className={`bg-white rounded-lg p-4 shadow border-t-4 border-${color}-500`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-medium text-gray-800">Share of Wallet</h3>
          <div className={`w-8 h-8 rounded-full bg-${color}-100 flex items-center justify-center`}>
            <PieChart size={16} className={`text-${color}-600`} />
          </div>
        </div>
        <div className="mt-1">
          <div className="text-2xl font-bold text-gray-900">{selectedSegment.shareOfWallet}%</div>
          <div className="flex items-center mt-2 text-xs">
            <div className={`px-2 py-1 rounded-full ${getStatusBg(shareStatus)} ${getStatusColor(shareStatus)} flex items-center`}>
              {getStatusIcon(shareStatus)}
              {shareStatus === 'above' ? '+' : shareStatus === 'below' ? '-' : ''}
              {Math.abs(selectedSegment.shareOfWallet - 70).toFixed(0)}% vs competitors
            </div>
          </div>
        </div>
      </div>
      
      {/* NFR Sales */}
      <div className={`bg-white rounded-lg p-4 shadow border-t-4 border-${color}-500`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-medium text-gray-800">NFR Sales</h3>
          <div className={`w-8 h-8 rounded-full bg-${color}-100 flex items-center justify-center`}>
            <BarChart2 size={16} className={`text-${color}-600`} />
          </div>
        </div>
        <div className="mt-1">
          <div className="text-2xl font-bold text-gray-900">{selectedSegment.nfrSales}%</div>
          <div className="flex items-center mt-2 text-xs">
            <div className={`px-2 py-1 rounded-full ${selectedSegment.nfrSales > 40 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'} flex items-center`}>
              {selectedSegment.nfrSales > 40 ? (
                <TrendingUp size={12} className="mr-1" />
              ) : (
                <TrendingDown size={12} className="mr-1" />
              )}
              {selectedSegment.nfrSales > 40 ? '+' : ''}{selectedSegment.nfrSales > 40 ? '6' : '-2'}% vs target
            </div>
          </div>
        </div>
      </div>
      
      {/* Loyalty */}
      <div className={`bg-white rounded-lg p-4 shadow border-t-4 border-${color}-500`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-medium text-gray-800">Customer Loyalty</h3>
          <div className={`w-8 h-8 rounded-full bg-${color}-100 flex items-center justify-center`}>
            <Users size={16} className={`text-${color}-600`} />
          </div>
        </div>
        <div className="mt-1">
          <div className="text-2xl font-bold text-gray-900">{selectedSegment.loyalty}%</div>
          <div className="flex items-center mt-2 text-xs">
            <div className={`px-2 py-1 rounded-full ${selectedSegment.loyalty > 40 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} flex items-center`}>
              {selectedSegment.loyalty > 40 ? (
                <TrendingUp size={12} className="mr-1" />
              ) : (
                <TrendingDown size={12} className="mr-1" />
              )}
              {selectedSegment.loyalty > 40 ? '+' : ''}{selectedSegment.loyalty > 40 ? '4' : '-5'}% vs avg
            </div>
          </div>
        </div>
      </div>
      
      {/* Customer Satisfaction (if included) */}
      {includeCustomerSatisfaction && (
        <div className={`bg-white rounded-lg p-4 shadow border-t-4 border-${color}-500 col-span-1 md:col-span-2 lg:col-span-4`}>
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-medium text-gray-800">Customer Satisfaction</h3>
            <div className={`px-3 py-1 rounded-full ${
              selectedSegment.satisfaction >= 90 ? 'bg-green-100 text-green-800' : 
              selectedSegment.satisfaction >= 85 ? 'bg-yellow-100 text-yellow-800' : 
              'bg-red-100 text-red-800'
            }`}>
              {selectedSegment.satisfaction}% Satisfaction
            </div>
          </div>
          
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className={`h-2.5 rounded-full ${
                  selectedSegment.satisfaction >= 90 ? 'bg-green-600' : 
                  selectedSegment.satisfaction >= 85 ? 'bg-yellow-500' : 
                  'bg-red-600'
                }`} 
                style={{ width: `${selectedSegment.satisfaction}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between mt-1 text-xs text-gray-500">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
          
          <div className="mt-3 text-sm text-gray-600">
            {selectedSegment.satisfaction >= 90 ? (
              'Excellent satisfaction score. Customers report positive experiences across all service areas.'
            ) : selectedSegment.satisfaction >= 85 ? (
              'Good satisfaction score. Minor improvements in customer service areas could further enhance customer experience.'
            ) : (
              'Needs improvement. Customer experience issues should be addressed to prevent churn and revenue loss.'
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentProfile;
